import { useMemo } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import {
    DEFAULT_AXNUI_CONTENT,
    DEFAULT_AXNUI_LAYOUT,
    DEFAULT_AXNUI_THEME
} from "@iddataweb/idw-common-ui/constants";
import { AttributeTypes, LocaleTypes } from "@iddataweb/idw-common-ui/enums";
import { AXNUIContent } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import {
    AXNUI_CONTENT_PROPERTY_TYPE_ID,
    AXNUI_LAYOUT_PROPERTY_TYPE_ID,
    AXNUI_THEME_PROPERTY_TYPE_ID,
    USE_THEMED_UI_PROPERTY_TYPE_ID
} from "@constants/verification-service-details-constants";
import { useModalContext } from "@context/use-modal-context";
import { ModalTypes } from "@enums/modal-types";
import {
    FormattedRpServiceConfiguration,
    FormattedRpServiceConfigurationAttribute,
    FormattedRpServiceConfigurationProperty
} from "@idw-types/data/rp-service-configuration";
import { generateValueForContentProperty } from "@utils/content-util";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { AddRpServiceConfigurationPropertyModal } from "@containers/modals/add-rp-service-configuration-property-modal/add-rp-service-configuration-property-modal";
import { RpServiceConfigurationPropertiesContainer } from "@containers/common/rp-service-configuration-properties-container/rp-service-configuration-properties-container";
import commonStyles from "../../verification-service-details-component.less";

type GatewayPropertiesTabContainerProps = {
    isDisabled: boolean;
    selectedAttributes: Array<FormattedRpServiceConfigurationAttribute>;
    selectedLanguage: LocaleTypes;
    availableLanguages: Array<LocaleTypes>;
    isMultiLanguageEnabled: boolean;
    rpServiceConfigurationState: FormattedRpServiceConfiguration;
    setRpServiceConfigurationState: (state: FormattedRpServiceConfiguration) => void;
};

export const GatewayPropertiesTabContainer = (props: GatewayPropertiesTabContainerProps) => {
    const {
        isDisabled,
        selectedAttributes,
        selectedLanguage,
        availableLanguages,
        isMultiLanguageEnabled,
        rpServiceConfigurationState,
        setRpServiceConfigurationState
    } = props;

    const modalContext = useModalContext();

    const gatewayPropertyIds = useMemo(() => {
        return rpServiceConfigurationState.properties.filter(property => property.category === "display").map(property => property.propertyTypeId);
    }, []);

    const permissionBasedIsDisabled = isDisabled || !rpServiceConfigurationState.permissions.canModifyRPServiceProperties;
    const unselectedGatewayProperties = rpServiceConfigurationState.properties.filter(property => gatewayPropertyIds.includes(property.propertyTypeId) && !property.addedToService);
    const selectedGatewayProperties = rpServiceConfigurationState.properties.filter(property => gatewayPropertyIds.includes(property.propertyTypeId) && property.addedToService);

    const addRpServiceConfigurationProperty = (rpServiceConfigurationProperty: FormattedRpServiceConfigurationProperty) => {
        const newProperties = rpServiceConfigurationState.properties.map(property => {
            // if we find the property, update it
            if (property.propertyTypeId === rpServiceConfigurationProperty.propertyTypeId) {
                return rpServiceConfigurationProperty;
            } else if (rpServiceConfigurationProperty.propertyTypeId === USE_THEMED_UI_PROPERTY_TYPE_ID) {
                // if the new property is useThemedUI, we need to add the AXNUI theming, layout and content properties
                if (property.propertyTypeId === AXNUI_CONTENT_PROPERTY_TYPE_ID) {
                    const attributesToStore: Partial<{ [key in AttributeTypes]: string; }> = {};

                    selectedAttributes.forEach(attr => {
                        if (
                            DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH].labels.attributes[attr.attributeType?.name] &&
                            attr.attributeLabel?.label
                        ) {
                            attributesToStore[attr.attributeType.name] = attr.attributeLabel.label
                        }
                    });

                    const newAXNUIContent: AXNUIContent = {
                        ...DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH],
                        labels: {
                            attributes: {
                                ...DEFAULT_AXNUI_CONTENT[LocaleTypes.ENGLISH].labels.attributes,
                                ...attributesToStore
                            }
                        }
                    };

                    const newValue = generateValueForContentProperty(
                        isMultiLanguageEnabled,
                        DEFAULT_AXNUI_CONTENT,
                        availableLanguages,
                        selectedLanguage,
                        newAXNUIContent,
                        DEFAULT_AXNUI_CONTENT
                    );

                    return {
                        ...property,
                        addedToService: true,
                        value: newValue
                    };
                } else if (property.propertyTypeId === AXNUI_LAYOUT_PROPERTY_TYPE_ID) {
                    return {
                        ...property,
                        addedToService: true,
                        value: JSON.stringify(DEFAULT_AXNUI_LAYOUT)
                    };
                } else if (property.propertyTypeId === AXNUI_THEME_PROPERTY_TYPE_ID) {
                    return {
                        ...property,
                        addedToService: true,
                        value: JSON.stringify(DEFAULT_AXNUI_THEME)
                    };
                }
            }
            return property;
        });

        setRpServiceConfigurationState({
            ...rpServiceConfigurationState,
            properties: newProperties
        });
    };

    const onClickAddGatewayProperty = () => {
        handleRenderAddPropertyModal(unselectedGatewayProperties);
    };

    const gatewayPropertiesResources = resources.pages.verificationServiceDetails.tabs.general.subTabs.advanced.sections.properties.sections.gatewayProperties;

    const handleRenderAddPropertyModal = (properties: Array<FormattedRpServiceConfigurationProperty>) => {
        const renderAddPropertyModal = (onModalClose: () => void) => {
            return (
                <AddRpServiceConfigurationPropertyModal
                    title={gatewayPropertiesResources.buttons.addProperty.label}
                    rpServiceConfigurationProperties={properties}
                    addRpServiceConfigurationProperty={addRpServiceConfigurationProperty}
                    onModalClose={onModalClose}
                />
            );
        };

        modalContext.setModalState({
            renderFunction: renderAddPropertyModal,
            modalType: ModalTypes.AddProperty
        });
    };

   
    const gatewayPropertiesHeaderButton = (
        (!permissionBasedIsDisabled) ? (
            <Button disabled={unselectedGatewayProperties.length === 0} onClick={onClickAddGatewayProperty}>
                {gatewayPropertiesResources.buttons.addProperty.label}
            </Button>
        ) : null
    );


    return (
        <div>
            <ContentPageHeader
                title={gatewayPropertiesResources.title}
                headerButton={gatewayPropertiesHeaderButton}
            />
            <ContentPageBody isMaxWidth={true}>
                <span className={commonStyles.description}>
                    {gatewayPropertiesResources.description}
                </span>
                    <div>
                        {unselectedGatewayProperties.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.allPropertiesMessage(
                                gatewayPropertiesResources.propertyType
                            )}</Alert>
                        )}
                        {selectedGatewayProperties.length === 0 && (
                            <Alert variant={"info"}>{resources.pages.verificationServiceDetails.tabs.common.alerts.noPropertiesMessage(
                                gatewayPropertiesResources.propertyType,
                                permissionBasedIsDisabled
                            )}</Alert>
                        )}
                        {selectedGatewayProperties.length > 0 && (
                            <RpServiceConfigurationPropertiesContainer
                                isDisabled={permissionBasedIsDisabled}
                                selectedRpServiceConfigurationProperties={selectedGatewayProperties}
                                rpServiceConfigurationState={rpServiceConfigurationState}
                                setRpServiceConfigurationState={setRpServiceConfigurationState}
                            />
                        )}
                    </div>
            </ContentPageBody>
        </div>
    );
};